import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlagAssetModal } from './FlagAssetModal';
import {
  MapPin,
  AlertCircle,
  CheckCircle2,
  Clock,
  Flag,
  Wrench,
  MessageSquarePlus,
  Eye,
  Camera,
  Package,
  AlertTriangle,
  XCircle,
  FileText,
  MoreVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AssetAuditProgress } from '@/types/audit';

interface AssetProgressCardProps {
  asset: AssetAuditProgress;
  onFlagForReview: (assetId: string, updatedNotes?: string) => void;
  onAssignWorkItem: (assetId: string) => void;
  onAddComment: (assetId: string) => void;
  onViewDetails: (assetId: string) => void;
}

export function AssetProgressCard({
  asset,
  onFlagForReview,
  onAssignWorkItem,
  onAddComment,
  onViewDetails,
}: Readonly<AssetProgressCardProps>) {
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  const [showFlagModal, setShowFlagModal] = useState(false);

  const handleFlagConfirm = (reason: string) => {
    // Get current user and timestamp
    const currentUser = 'John Smith'; // TODO: Get from auth context
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    // Create the flag note
    const flagNote = `${reason} - ${currentUser}, ${timestamp}`;

    // Append to existing notes or create new notes
    const updatedNotes = asset.notes 
      ? `${asset.notes}\n\n${flagNote}`
      : flagNote;

    // Call the parent handler with updated notes
    if (onFlagForReview) {
      onFlagForReview(asset.id, updatedNotes);
    }
  };

  const handleFlagClick = () => {
    if (asset.status === 'flagged') {
      // If already flagged, unflag it
      if (onFlagForReview) {
        onFlagForReview(asset.id);
      }
    } else {
      // Show modal to get flag reason
      setShowFlagModal(true);
    }
  };

  const getStatusConfig = () => {
    switch (asset.status) {
      case 'verified':
        return {
          icon: <CheckCircle2 className="h-4 w-4" />,
          label: 'Verified',
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-gray-200',
          badgeClass: 'bg-green-100 text-green-800 border-green-200',
        };
      case 'in-progress':
        return {
          icon: <Clock className="h-4 w-4" />,
          label: 'In Progress',
          color: 'text-purple-700',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
        };
      case 'not-started':
        return {
          icon: <Clock className="h-4 w-4" />,
          label: 'Not Started',
          color: 'text-blue-700',
          bgColor: 'bg-blue-50',
          borderColor: 'border-gray-200',
          badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
        };
      case 'flagged':
        return {
          icon: <Flag className="h-4 w-4" />,
          label: 'Flagged',
          color: 'text-orange-700',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
        };
      case 'missing':
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          label: 'Missing',
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          badgeClass: 'bg-red-100 text-red-800 border-red-200',
        };
      default:
        // Default to not started status
        return {
          icon: <Clock className="h-4 w-4" />,
          label: 'Not Started',
          color: 'text-gray-700',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          badgeClass: 'bg-gray-100 text-gray-800 border-gray-200',
        };
    }
  };

  const getConditionConfig = () => {
    switch (asset.condition) {
      case 'good':
        return {
          icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
          label: 'Good',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-900',
          badgeClass: 'bg-green-100 text-green-800 border-green-200',
        };
      case 'attention':
        return {
          icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
          label: 'Needs Attention',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-900',
          badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        };
      case 'critical':
        return {
          icon: <XCircle className="h-4 w-4 text-red-600" />,
          label: 'Critical',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-900',
          badgeClass: 'bg-red-100 text-red-800 border-red-200',
        };
      default:
        return null;
    }
  };

  const getLocationStatusConfig = () => {
    switch (asset.locationStatus) {
      case 'confirmed':
        return {
          icon: <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />,
          label: 'Confirmed',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-900',
        };
      case 'changed':
        return {
          icon: <AlertCircle className="h-3.5 w-3.5 text-amber-600" />,
          label: 'Changed',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-900',
        };
      default:
        return {
          icon: <Clock className="h-3.5 w-3.5 text-gray-400" />,
          label: 'Pending',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-900',
        };
    }
  };

  const statusConfig = getStatusConfig();
  const conditionConfig = getConditionConfig();
  const locationConfig = getLocationStatusConfig();

  return (
    <>
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-200 hover:shadow-lg border',
        asset.status === 'flagged' ? 'border-l-4 border-l-red-500' : ''
      )}
    >
      <div className="p-5">
        {/* Header with Asset Icon, Info, and Action Buttons */}
        <div className="flex items-start gap-4 mb-4">
          {/* Asset Icon */}
          <div className={cn('flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center', statusConfig.bgColor)}>
            <Package className={cn('h-6 w-6', statusConfig.color)} />
          </div>

          {/* Asset Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-base text-gray-900 truncate">
                {asset.assetName}
              </h3>
              <Badge variant="outline" className={cn('gap-1.5 font-medium flex-shrink-0', statusConfig.badgeClass)}>
                {statusConfig.icon}
                {statusConfig.label}
              </Badge>
              {asset.flaggedForReview && (
                <Badge variant="destructive" className="gap-1.5 font-medium flex-shrink-0">
                  <Flag className="h-3 w-3" />
                  Review Required
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-gray-600 font-medium">{asset.assetCode}</p>
              <span className="text-gray-300">•</span>
              <p className="text-sm text-gray-500">{asset.category}</p>
            </div>
          </div>

          {/* Action Buttons - Top Right */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* View Details - Always visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(asset.id)}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              View Details
            </Button>

            {/* Other actions in ellipsis dropdown menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleFlagClick}>
                  <Flag className="h-4 w-4 mr-2" />
                  {asset.status === 'flagged' ? 'Unflag' : 'Flag'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddComment(asset.id)}>
                  <MessageSquarePlus className="h-4 w-4 mr-2" />
                  Add Comment
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAssignWorkItem(asset.id)}>
                  <Wrench className="h-4 w-4 mr-2" />
                  Raise Work Item
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Horizontal Divider */}
        <div className="border-t border-gray-200 my-4" />

        {/* Key Information - Two Column Layout */}
        <div className="flex gap-4">
          {/* Left Column - Location, Condition, Notes */}
          <div className="flex-1 space-y-3">
            {/* Location and Condition - Same line on larger displays */}
            <div className="flex flex-col md:flex-row gap-3">
              {/* Location */}
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center gap-2 w-24 flex-shrink-0 pt-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-semibold text-gray-700">
                    Location
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  {asset.locationStatus === 'pending' ? (
                    <div className={cn('border rounded-md px-3 py-2', locationConfig.bgColor, locationConfig.borderColor)}>
                      <p className={cn('text-sm font-medium', locationConfig.textColor)}>Awaiting assessment</p>
                    </div>
                  ) : (
                    <div className={cn('border rounded-md px-3 py-2', locationConfig.bgColor, locationConfig.borderColor)}>
                      <div className="flex items-center gap-2">
                        <p className={cn('text-sm font-medium', locationConfig.textColor)}>{asset.currentLocation}</p>
                        {locationConfig.icon}
                      </div>
                      {asset.locationStatus === 'changed' && asset.previousLocation && (
                        <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-gray-200">
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                            Changed
                          </Badge>
                          <p className="text-xs text-gray-500">
                            from {asset.previousLocation}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Condition */}
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center gap-2 w-24 flex-shrink-0 pt-2">
                  <AlertCircle className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-semibold text-gray-700">
                    Condition
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  {conditionConfig ? (
                    <div className={cn('border rounded-md px-3 py-2 flex items-center gap-2', conditionConfig.bgColor, conditionConfig.borderColor)}>
                      <p className={cn('text-sm font-medium', conditionConfig.textColor)}>{conditionConfig.label}</p>
                      {conditionConfig.icon}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                      <p className="text-sm font-medium text-gray-900">Awaiting assessment</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 w-24 flex-shrink-0 pt-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <p className="text-sm font-semibold text-gray-700">
                  Notes
                </p>
              </div>
              <div className="flex-1 min-w-0">
                {asset.notes && asset.notes.trim() !== '' ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 min-h-[140px]">
                    <div className="text-sm font-medium text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
                      {asset.notes}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 min-h-[140px] flex items-start">
                    <p className="text-sm font-medium text-gray-900 italic">No notes provided</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Photos (2x2 grid, hidden on small devices) */}
          <div className="hidden lg:block w-48 flex-shrink-0">
            {asset.photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {asset.photos.slice(0, 4).map((photo, index) => (
                  <button
                    type="button"
                    key={photo.id}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all group"
                    onClick={() => onViewDetails(asset.id)}
                  >
                    {!imageError[photo.id] ? (
                      <>
                        <img
                          src={photo.thumbnailUrl}
                          alt={photo.caption || `Photo ${index + 1}`}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          onError={() => setImageError({ ...imageError, [photo.id]: true })}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                        {index === 3 && asset.photos.length > 4 && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-white text-lg font-bold">
                              +{asset.photos.length - 4}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="h-6 w-6 text-gray-300" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {/* Placeholder images */}
                {[1, 2, 3, 4].map((index) => (
                  <div
                    key={`placeholder-${index}`}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-200 flex items-center justify-center"
                  >
                    <Camera className="h-4 w-4 text-gray-300" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>

    <FlagAssetModal
      open={showFlagModal}
      onOpenChange={setShowFlagModal}
      onConfirm={handleFlagConfirm}
      assetName={asset.name ?? asset.assetName}
    />
  </>
  );
}
